import { SpanStatusCode } from '@opentelemetry/api';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { TenderType } from 'src/common/enumerations/tender-type';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ITender } from 'src/common/interfaces/dtos/ITender';
import { store } from 'src/common/redux/configure-store';
import { BRMInputActions } from 'src/common/redux/reducers/brm-input-reducer';
import { ticketTotal } from 'src/home-page/business-logic/service/ticket-total';
import { exitTenderingMode } from 'src/tendering/business-logic/tendering-actions';
import { EmeraldError } from '../../common/api/emerald-error';
import { DataAddRequired, NoPriceExistPerUnitOfMeasure } from '../../common/helpers/error-code';
import XLRLogger from '../../common/helpers/logger';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { addTransactionDataLogic } from '../../home-page/business-logic/add-transaction-data-logic';
import { addLookupLineItem } from './add-lookup-line-item';
import { ILineItemAddData, lineItemAdd } from './line-item-add';
import { getLineItem } from './line-item-builder';

export const addTransactionLine = async (inputData: IItemInputData): Promise<void> => {
  const lineItemAddData: ILineItemAddData = createDataWithLineItem(inputData);

  const rawTenders = store.getState().tenderMenu.sellableTenders.rawTenders;
  const { isTransactionTotaled, transactionId } = store.getState().transaction;
  let enterTenderingMode = false;
  const span = createSpan(`add-transaction-line (itemId: ${inputData.itemId})`);

  return withSpinner(() =>
    lineItemAdd(lineItemAddData)
      .catch(async err => {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: 'addTransactionLine error',
        });
        if (err instanceof EmeraldError) {
          if (err.code === NoPriceExistPerUnitOfMeasure) {
            enterTenderingMode = false;
            return addLookupLineItem({ inputData });
          }
          if (err.code === DataAddRequired) {
            let matchingTender: ITender = { id: '' };
            if (err.data.dataInformation.type === 'Tender') {
              matchingTender =
                rawTenders.find(i => i.id === err.data.dataInformation.tenderId) ||
                err.data.dataInformation.tenderId;
            }
            if (
              matchingTender.type &&
              matchingTender.type === TenderType.Voucher &&
              isTransactionTotaled
            ) {
              enterTenderingMode = true;
              await ticketTotal(transactionId);
            } else {
              enterTenderingMode = false;
            }
            return addTransactionDataLogic(err.data.dataInformation, inputData);
          }
          throw err;
        }

        XLRLogger.error({
          message: `An unexpected non-business error occurred during command execution.`,
          source: 'add-transaction-line',
          data: err,
        });
        throw new EmeraldError(
          'GeneralError',
          'General error occurred. Please contact your System Administrator',
        );
      })
      .finally(async () => {
        if (isTransactionTotaled && !enterTenderingMode) {
          await exitTenderingMode();
        }
        store.dispatch(BRMInputActions.resetState());
        endSpan(span);
      }),
  );
};

const createDataWithLineItem = (inputData: IItemInputData): ILineItemAddData => {
  const lineItem = getLineItem(inputData);
  lineItem.withData = true;
  return { inputData, itemLookup: {}, lineItem };
};
